import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import io from 'socket.io-client';
import useAuthContext from './AuthContext';

const SocketContext = createContext({});

/**
 * Provides a context for managing WebSocket connections and connected users.
 *
 * @param {Object} props - React component props.
 * @param {ReactNode} props.children - The child components that will have access to the SocketContext.
 * @returns {JSX.Element} The SocketContextProvider component.
 */
export const SocketContextProvider = ({ children }) => {
  const { token } = useAuthContext();
  const [socket, setSocket] = useState();
  const [connectedUsers, setConnectedUsers] = useState([]);

  useEffect(() => {
    if (token) {
      setSocket(io(`${process.env.REACT_APP_API_URL}?token=${token}`));
    } else if (socket && !token) {
      socket.emit('logout');
      socket.close();
    }
    return () => {
      if (socket) socket.close();
    };
  }, [token]);

  useEffect(() => {
    if (socket)
      socket.on('connectedUsers', (users) => setConnectedUsers(users));
  }, [socket]);

  return (
    <SocketContext.Provider value={{ socket, connectedUsers }}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * A custom hook for accessing the SocketContext within functional components.
 *
 * @returns {Object} An object containing the WebSocket socket and connected users.
 * @throws {Error} Throws an error if used outside of a SocketContextProvider.
 */
export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (context === undefined)
    throw new Error('Context must be used within a context provider');
  return context;
};

SocketContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};
